import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import styles from "./SingleBrandPage.module.css";

import AgeSign from "../../asset/icons/18PlusSign.png";
import RupeeCycle from "../../asset/icons/RupyeeCycle.png";
import FleekTiltIcon from "../../asset/icons/fleekTiltIcon.png";
import { ReadMoreComp } from "../../components/ReadMoreComp/ReadMoreComp";
import { ErrorFallback, createErrorDetails } from "../../components/Error/Error";
// import { Loading } from "../../components/Loading/Loading"; // Not used
import {
  DigitalBrandPlan,
  BrandAgeConBottomSheet,
  TermNConditions,
  D2CSubscriptionBoxCard,
  BrandUserDetailsBottomSheet,
} from "../../components";
import { BrandImgInfoBanner } from "../../commonComponents";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import UTMTracker from "../../utils/UTMTracker";

// Lazy load heavy components with proper default exports
const DigitalBrandBenefit = lazy(() => 
  import("../../components/DigitalBrandBenefit/DigitalBrandBenefit.jsx").then(module => ({
    default: module.DigitalBrandBenefit
  }))
);
const DigitalBrandFaq = lazy(() => 
  import("../../components/DigitalBrandFaq/DigitalBrandFaq.jsx").then(module => ({
    default: module.DigitalBrandFaq
  }))
);

// Remove direct analytics imports - will be lazy loaded
// import ConfettiHelper from '../../utils/ConfettiHelper';
// import * as amplitude from '@amplitude/analytics-browser';
// import { trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS } from '../../utils/analyticsService';

// Simplified skeleton loader for better performance
const BrandPageSkeleton = ({ isRetrying = false, retryCount = 0 }) => {
  return (
    <div className={styles.brandPage}>
      {/* Simplified Banner Skeleton */}
      <div className={styles.bannerSkeleton}>
        <div className={styles.bannerImageSkeleton}></div>
        <div className={styles.bannerContentSkeleton}>
          <div className={styles.logoSkeleton}></div>
          <div className={styles.brandNameSkeleton}></div>
        </div>
      </div>
      
      {/* Simplified Content Skeleton */}
      <div className={styles.content}>
        {/* Plans Section Skeleton - reduced elements */}
        <div className={styles.planSection}>
          <div className={styles.sectionTitleSkeleton}></div>
          <div className={styles.planList}>
            {[1, 2].map((_, idx) => ( // Reduced from 3 to 2
              <div key={idx} className={styles.planCardSkeleton}>
                <div className={styles.planHeaderSkeleton}></div>
                <div className={styles.planPriceSkeleton}></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Retry indicator */}
        {isRetrying && (
          <div className={styles.retryIndicator}>
            <div className={styles.retrySpinner}></div>
            <p className={styles.retryText}>
              {retryCount === 1 ? 'Retrying...' : `Retry attempt ${retryCount}...`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const SingleBrandPage = ({ isLoggedIn }) => {
  const { vendor_name } = useParams();
  const navigate = useNavigate();

  const [brandData, setBrandData] = useState({
    name: "",
    banner_img: "",
    logo: "",
    desc: "",
    plans: [],
    termNCond: [],
    faqList: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [extraDetailsReq, setExtraDetailsReq] = useState(false);
  const [extraDetailsData, setExtraDetailsData] = useState({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const [modalData, setModalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [selectedPlanIdx, setSelectedPlan] = useState(-1);
  const [isD2c] = useState(false); // Removed setIsD2c as it's not used
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const MAX_RETRIES = 2;

  const boxes = [1, 2, 3, 4, 5, 6];
  localStorage.removeItem("bundleId");

  const toggleModal = useCallback(() => {
    setShowModal((pre_state) => !pre_state);
  }, []);

  const AddToCart = useCallback(async (details) => {
    try {
      console.log("SingleBrandPage AddToCart called");
      
      // Handle Chess.com affiliate redirect
      if (vendor_name === 'chess.com') {
        // Lazy load analytics
        const { trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS } = await import('../../utils/analyticsService');
        trackCustomEvent(CUSTOM_EVENTS.EXTERNAL_LINK_CLICKED, {
          link_name: 'Chess.com Affiliate',
          source: 'buy_now_button',
          component: 'SingleBrandPage',
          vendor_name: vendor_name,
          affiliate_url: 'https://chess.com/membership?ref_id=372424947',
          selected_plan_idx: selectedPlanIdx
        }, ANALYTICS_PROVIDERS.AMPLITUDE);
        
        window.open('https://chess.com/membership?ref_id=372424947', '_blank');
        return;
      }
      
      // Get selected plan data early for use throughout function
      const selectedPlan = brandData.plans[selectedPlanIdx];
      
      // Prevent multiple clicks
      if (isAddingToCart) return;
      setIsAddingToCart(true);
      
      // Lazy load confetti
      const { default: ConfettiHelper } = await import('../../utils/ConfettiHelper');
      ConfettiHelper.smallBlast();
      
      if (!isLoggedIn) {
        // Lazy load analytics
        const { trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS } = await import('../../utils/analyticsService');
        trackCustomEvent(CUSTOM_EVENTS.BUTTON_CLICKED, {
          button_name: 'Add to Cart',
          login_required: true,
          trigger_source: 'add_to_cart',
          plan_id: details?.subscription_plan || 'unknown',
          plan_name: selectedPlan?.plan_name || 'unknown',
          value: selectedPlan?.discounted_cost || 0,
          currency: selectedPlan?.currency_type || 'INR',
          page_url: window.location.href
        }, ANALYTICS_PROVIDERS.AMPLITUDE);
        
        // Save more complete redirect information
        localStorage.setItem("brandId", vendor_name);
        localStorage.setItem("returnToPage", `/brand/${vendor_name}`);
        localStorage.setItem("pendingAction", "addToCart");
        localStorage.setItem("pendingActionDetails", JSON.stringify({
          subscription_plan: details.subscription_plan,
          quantity: details.quantity,
          selectedPlanIdx: selectedPlanIdx
        }));
        
        console.log("User not logged in, redirecting to login with return path set");
        setIsAddingToCart(false);
        return navigate("/login");
      }

      if (brandData.activeSubscription) {
        setShowModal(true);
        setIsAddingToCart(false);
        return;
      }
      
      if (
        brandData.extra_details_required &&
        brandData.extra_details_required.length &&
        !(
          brandData.extra_details_required.length + 1 ===
          Object.keys(extraDetailsData).length
        )
      ) {
        setExtraDetailsReq(true);
        setExtraDetailsData((pre_state) => {
          return { ...pre_state, ...details };
        });
        setIsAddingToCart(false);
        return;
      }
      
      //remove all items from cart
      await Api.EmptyCart();
      
      // add plan to cart
      await Api.AddToCart(details);

      // Lazy load confetti for celebration
      const { default: ConfettiHelperCelebration } = await import('../../utils/ConfettiHelper');
      ConfettiHelperCelebration.purchaseCelebration();
      
      navigate("/checkout");
    } catch (err) {
      console.log("Error in AddToCart:", err);
      setIsAddingToCart(false);
    }
  }, [vendor_name, selectedPlanIdx, brandData.plans, brandData.activeSubscription, brandData.extra_details_required, isAddingToCart, isLoggedIn, extraDetailsData, navigate]);

  useEffect(() => {
    // Capture UTM parameters on direct landing
    UTMTracker.captureUTMParams();
    
    // Check if we're returning from login with a pending action
    const pendingAction = localStorage.getItem("pendingAction");
    const pendingDetails = localStorage.getItem("pendingActionDetails");
    
    if (isLoggedIn && pendingAction === "addToCart" && pendingDetails) {
      try {
        console.log("Continuing pending addToCart action after login");
        
        // AMPLITUDE USER IDENTIFICATION FOR RETURNING USERS - Lazy loaded
        const phoneNo = localStorage.getItem('phoneNo');
        if (phoneNo) {
          // Lazy load amplitude
          import('@amplitude/analytics-browser').then((amplitude) => {
            amplitude.setUserId(phoneNo);
          });
          
          // Lazy load analytics for Post-Login Action
          import('../../utils/analyticsService').then(({ trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS }) => {
            trackCustomEvent(CUSTOM_EVENTS.BUTTON_CLICKED, {
              button_name: 'Add to Cart',
              action: 'add_to_cart',
              return_source: 'login_flow',
              brand_name: vendor_name,
              page_url: window.location.href
            }, ANALYTICS_PROVIDERS.AMPLITUDE);
          });
        }
        
        const details = JSON.parse(pendingDetails);
        
        // Clean up localStorage
        localStorage.removeItem("pendingAction");
        localStorage.removeItem("pendingActionDetails");
        
        // Set the selected plan index first (important for UI state)
        if (details.selectedPlanIdx !== undefined) {
          setSelectedPlan(details.selectedPlanIdx);
        }
        
        // Add a small delay to ensure component is fully mounted
        setTimeout(() => {
          // Add to cart with the saved details
          AddToCart({
            subscription_plan: details.subscription_plan,
            quantity: details.quantity
          });
        }, 500);
      } catch (err) {
        console.error("Error processing pending action:", err);
      }
    }
    
    const fetchBrandDataWithRetry = async (attempt = 0) => {
      try {
        // Handle Chess.com redirect before making API call
        if (vendor_name === 'chess.com') {
          // Lazy load analytics for Chess.com affiliate click
          import('../../utils/analyticsService').then(({ trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS }) => {
            trackCustomEvent(CUSTOM_EVENTS.EXTERNAL_LINK_CLICKED, {
              link_name: 'Chess.com Affiliate',
              source: 'direct_page_access',
              component: 'SingleBrandPage',
              vendor_name: vendor_name,
              affiliate_url: 'https://chess.com/membership?ref_id=372424947'
            }, ANALYTICS_PROVIDERS.AMPLITUDE);
          });
          
          window.location.href = 'https://chess.com/membership?ref_id=372424947';
          return;
        }
        
        // Set retry state
        if (attempt > 0) {
          setIsRetrying(true);
          setRetryCount(attempt);
          
          // Exponential backoff delay
          const delay = 1000;
          console.log(`Retrying API call for ${vendor_name}, attempt ${attempt}, delay: ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Lazy load analytics for retry attempt tracking
          import('../../utils/analyticsService').then(({ trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS }) => {
            trackCustomEvent(CUSTOM_EVENTS.ERROR_OCCURRED, {
              error_type: 'api_retry_attempt',
              vendor_name: vendor_name,
              retry_attempt: attempt,
              max_retries: MAX_RETRIES,
              delay_ms: delay,
              user_source: document.referrer || 'direct',
              is_mobile: /Mobi|Android/i.test(navigator.userAgent)
            }, ANALYTICS_PROVIDERS.AMPLITUDE);
          });
        }
        
        const response = await Api.BrandDetails({
          params: {
            vendor_name,
            show_out_of_stock: true,
            extra_details_for_web: true,
          },
        });

        // Validate API response structure
        if (!response || !response.data) {
          throw new Error(`Invalid API response: ${JSON.stringify(response)}`);
        }

        const { vendor_data, plans_data } = response.data;

        // Validate required data
        if (!vendor_data) {
          throw new Error(`Missing vendor_data for brand: ${vendor_name}`);
        }

        if (!plans_data || !Array.isArray(plans_data)) {
          throw new Error(`Invalid plans_data for brand: ${vendor_name}`);
        }
        
        // Reset retry states on success
        setIsRetrying(false);
        setRetryCount(0);
        
        // Set loading to false on successful data load
        setLoading(false);

        // Track ViewContent event when plans are loaded
        if (plans_data?.length) {
          import('../../utils/analyticsService').then(({ trackViewContent }) => {
            trackViewContent({
              content_ids: plans_data.map(plan => plan.subscription_plan_id),
              content_type: plans_data.length > 1 ? 'product_group' : 'product',
              content_name: vendor_data.service.service_name,
              value: plans_data[0]?.discounted_cost || plans_data[0]?.cost || 0,
              currency: plans_data[0]?.currency_type || 'INR',
              content_category: 'subscription_plan'
            });
          });
        }

        // Check if any plan has USD currency and handle auto-login immediately
        const hasUSDPlan = plans_data.some(plan => plan.currency_type === 'usd');
        if (hasUSDPlan) {
          await Helper.checkAndHandleUSDCurrency('usd');
          // If we're not logged in and this is a USD plan, don't proceed with setting state
          // The page will reload after auto-login
          if (!localStorage.getItem('accesstoken')) {
            return;
          }
        }

        setBrandData({
          name: vendor_data.service.service_name,
          banner_img: vendor_data.detail_page_banner_url,
          logo: vendor_data.service.logo_url,
          desc: vendor_data.description,
          plans: plans_data,
          termNCond: vendor_data.terms_and_conditions,
          activeSubscription: vendor_data.is_already_availed_in_subscribe_flow,
          extra_details_required: vendor_data.extra_details_required,
          metaDetails: {
            title: vendor_data?.seo_title || "",
            content: vendor_data?.seo_description || "",
          },
          discount_percentage: vendor_data.discount_percentage,
          faqList: vendor_data.faq,
          overView: vendor_data.overview,
          benefits: vendor_data.benefits,
        });
        if (vendor_data.show_age_restrictions_popup) {
          setShowModal(true);
          setModalData({
            title: "You need to be above 18 years of age",
            text: "Disclaimer: This subscription may contain adult language and content. It is not suitable for anyone under the age of 18 and may not be suitable for all adult viewers, view at your discretion.",
            button2: {
              text: "NO I'M NOT",
              action: "/marketplace",
            },
            button1: {
              text: "YES, I AM ABOVE 18",
              action: vendor_data.vendor_id,
            },
            type: 1,
            image: AgeSign,
          });
        } else if (vendor_data.is_already_availed_in_subscribe_flow) {
          // setShowModal(true);
          setModalData({
            title: `You already have a recurring subscription for ${
              vendor_data.service.service_name
            }. ${
              vendor_data.due_date
                ? `Your next purchase will be done on ${Helper.dateFormat(
                    vendor_data.due_date
                  )}`
                : ""
            }`,
            text: "",
            button2: {
              text: "OKAY, GOT IT",
              action: "",
              cta: true,
            },
            type: 2,
            image: RupeeCycle,
          });
        }
      } catch (err) {
        console.error(`SingleBrandPage error (attempt ${attempt + 1}):`, err);
        
        // If we haven't reached max retries, try again
        if (attempt < MAX_RETRIES) {
          console.log(`Retrying... (${attempt + 1}/${MAX_RETRIES})`);
          return fetchBrandDataWithRetry(attempt + 1);
        }
        
        // Final failure after all retries
        console.error(`All retries failed for ${vendor_name}`);
        
        // Lazy load analytics for enhanced error tracking
        import('../../utils/analyticsService').then(({ trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS }) => {
          trackCustomEvent(CUSTOM_EVENTS.ERROR_OCCURRED, {
            error_type: 'brand_details_fetch_failed_final',
            error_message: err.message,
            vendor_name: vendor_name,
            total_attempts: attempt + 1,
            max_retries: MAX_RETRIES,
            user_source: document.referrer || 'direct',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            connection_type: navigator.connection?.effectiveType || 'unknown',
            user_logged_in: isLoggedIn,
            page_load_time: performance.now(),
            timestamp: new Date().toISOString()
          }, ANALYTICS_PROVIDERS.AMPLITUDE);
        });
        
        setIsRetrying(false);
        setError(true);
        setErrorDetails(createErrorDetails(err, {
          componentName: 'SingleBrandPage',
          errorContext: 'brand_details_fetch_with_retries',
          apiCallsAttempted: ['BrandDetails'],
          additionalContext: {
            vendor_name: vendor_name,
            user_logged_in: isLoggedIn,
            total_retry_attempts: attempt + 1,
            max_retries: MAX_RETRIES,
            user_source: document.referrer || 'direct',
            is_mobile_browser: /Mobi|Android/i.test(navigator.userAgent),
            connection_info: {
              type: navigator.connection?.effectiveType || 'unknown',
              downlink: navigator.connection?.downlink || 'unknown',
              rtt: navigator.connection?.rtt || 'unknown'
            },
            url_params: {
              vendor_name,
              show_out_of_stock: true,
              extra_details_for_web: true
            },
            api_response_validation_failed: err.message.includes('Invalid API response') || err.message.includes('Missing vendor_data'),
            page_load_performance: performance.now()
          }
        }));
      } finally {
        // Only set loading to false if this is the final attempt (success or final failure)
        if (attempt >= MAX_RETRIES) {
          setLoading(false);
        }
      }
    };
    
    // Start the fetch with retry logic
    fetchBrandDataWithRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, vendor_name]);

  // Memoize processed plans for better performance
  const processedPlans = useMemo(() => {
    return brandData.plans?.map((plan, idx) => ({
      ...plan,
      index: idx,
      isSelected: selectedPlanIdx === idx
    })) || [];
  }, [brandData.plans, selectedPlanIdx]);

  if (hasError) {
    return <ErrorFallback errorDetails={errorDetails} />;
  }
  if (loading) {
    return <BrandPageSkeleton isRetrying={isRetrying} retryCount={retryCount} />;
  }
  const {
    benefits,
    plans,
    termNCond,
    overView,
    faqList,
    // activeSubscription, // Not used in render
    discount_percentage,
    extra_details_required,
    metaDetails = {
      title: "",
      content: "",
    },
  } = brandData;
  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{metaDetails.title}</title>
        <meta content={metaDetails.content} name="description" />
        <meta name="keywords" content={`${brandData.name}, subscription, digital subscription, streaming, OTT, discount, offers, Fleek`} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Fleek" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
        <meta name="theme-color" content="#00998C" />
        <meta name="color-scheme" content="dark light" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="format-detection" content="telephone=no" />
        <link rel="manifest" href="/manifest.json" />
        <meta content={metaDetails.title} property="twitter:title"></meta>
        <meta
          content={metaDetails.content}
          property="twitter:description"
        ></meta>
        <meta content="summary_large_image" name="twitter:card"></meta>
        <meta content={metaDetails.title} property="og:title"></meta>
        <meta content={metaDetails.content} property="og:description"></meta>
        <meta content={brandData.banner_img} property="og:image"></meta>
        <meta content={brandData.banner_img} name="twitter:image"></meta>
        <link rel="canonical" href={"/brand/" + vendor_name} />
        <meta property="og:type" content="website"></meta>
        
        {/* Critical resource preloading for LCP */}
        {brandData.banner_img && (
          <link
            rel="preload"
            as="image"
            href={brandData.banner_img}
            fetchpriority="high"
          />
        )}
        {brandData.logo && (
          <link
            rel="preload"
            as="image"
            href={brandData.logo}
            fetchpriority="high"
          />
        )}
        
        {/* DNS prefetch for external domains */}
        <link rel="dns-prefetch" href="//res.cloudinary.com" />
        <link rel="dns-prefetch" href="//images.unsplash.com" />
        
        {/* Preconnect to critical domains */}
        <link rel="preconnect" href="//res.cloudinary.com" crossorigin />
        <link rel="preconnect" href="//fonts.googleapis.com" crossorigin />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Product",
                "@id": `${window.location.origin}/brand/${vendor_name}#product`,
                "name": brandData.name,
                "description": brandData.desc,
                "image": [brandData.logo, brandData.banner_img].filter(Boolean),
                "brand": {
                  "@type": "Brand",
                  "@id": `${window.location.origin}/brand/${vendor_name}#brand`,
                  "name": brandData.name,
                  "logo": {
                    "@type": "ImageObject",
                    "url": brandData.logo,
                    "width": 56,
                    "height": 56
                  }
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.5",
                  "reviewCount": "100",
                  "bestRating": "5",
                  "worstRating": "1"
                },
                "offers": plans.map(plan => ({
                  "@type": "Offer",
                  "@id": `${window.location.origin}/brand/${vendor_name}#offer-${plan.subscription_plan_id}`,
                  "price": plan.discounted_cost,
                  "priceCurrency": plan.currency_type || "INR",
                  "name": plan.plan_name,
                  "availability": "https://schema.org/InStock",
                  "validFrom": new Date().toISOString(),
                  "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                  "seller": {
                    "@type": "Organization",
                    "name": "Fleek",
                    "url": window.location.origin
                  }
                })),
                "category": "Digital Subscription",
                "url": `${window.location.origin}/brand/${vendor_name}`
              },
              {
                "@type": "WebPage",
                "@id": `${window.location.origin}/brand/${vendor_name}#webpage`,
                "url": `${window.location.origin}/brand/${vendor_name}`,
                "name": metaDetails.title,
                "description": metaDetails.content,
                "isPartOf": {
                  "@type": "WebSite",
                  "@id": `${window.location.origin}#website`,
                  "name": "Fleek - Digital Subscriptions",
                  "url": window.location.origin
                },
                "primaryImageOfPage": {
                  "@type": "ImageObject",
                  "url": brandData.banner_img,
                  "width": 1200,
                  "height": 630
                },
                "datePublished": new Date().toISOString(),
                "dateModified": new Date().toISOString()
              },
              {
                "@type": "BreadcrumbList",
                "@id": `${window.location.origin}/brand/${vendor_name}#breadcrumb`,
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": window.location.origin
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Brands",
                    "item": `${window.location.origin}/marketplace`
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": brandData.name,
                    "item": `${window.location.origin}/brand/${vendor_name}`
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {extraDetailsReq && (
        <BrandUserDetailsBottomSheet
          setExtraDetailsReq={setExtraDetailsReq}
          extra_details_required={extra_details_required}
          AddToCart={AddToCart}
          extraDetailsData={extraDetailsData}
          setExtraDetailsData={setExtraDetailsData}
        />
      )}
      {showModal && (
        <BrandAgeConBottomSheet
          toggleModal={toggleModal}
          modalData={modalData}
        />
      )}

      <div className={styles.displayDesktop}>{/* <NavBar/> */}</div>
      <div className={styles.brandPage}>
        <BrandImgInfoBanner isLoggedIn={isLoggedIn} brandData={brandData} />
        {!isD2c && (
          <div className={styles.content}>
            {discount_percentage ? (
              <div className={styles.fleekCoinContainer}>
                <div className={styles.fleekTextContainer}>
                  <div className={styles.fleekText}>
                    Get{" "}
                    <p className={styles.fleekBoldText}>
                      {discount_percentage}%
                    </p>{" "}
                    OFF
                  </div>
                  {/* <p className={styles.fleekOtherText}>using FleekCoins</p> */}
                </div>
                <img
                  className={styles.fleekTiltIcon}
                  src={FleekTiltIcon}
                  alt="FleekTiltIcon"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            <section className={styles.planSection} aria-labelledby="plans-heading">
              <h2 id="plans-heading" className={styles.sectionTitle}>Available Plans</h2>
              <div className={styles.planList} role="group" aria-label="Subscription plans">
                {processedPlans.map((ele) => (
                  <DigitalBrandPlan
                    AddToCart={AddToCart}
                    plan={ele}
                    index={ele.index}
                    selectedPlanIdx={selectedPlanIdx}
                    setSelectedPlan={setSelectedPlan}
                    key={ele.index}
                  />
                ))}
              </div>
            </section>
            {termNCond.length > 0 && <TermNConditions terms={termNCond} />}
            {overView && overView.length && (
              <section className={styles.overViewSection} aria-labelledby="overview-heading">
                <h2 id="overview-heading" className={styles.sectionTitle}>Overview</h2>
                <div className={styles.overViewText} role="region" aria-label="Product overview">
                  <ReadMoreComp cut={120} text={overView} />
                </div>
              </section>
            )}
            {selectedPlanIdx !== -1 && (
              <div className={styles.mobileButtonContainer}>
                <button
                  onClick={() =>
                    AddToCart({
                      subscription_plan: Number(
                        plans[selectedPlanIdx].subscription_plan_id
                      ),
                      quantity: plans[selectedPlanIdx].quantity,
                    })
                  }
                  className={`${styles.payButton} ${isAddingToCart ? styles.loadingButton : ''}`}
                  disabled={isAddingToCart}
                  aria-label={`Buy ${plans[selectedPlanIdx]?.plan_name} plan for ₹${plans[selectedPlanIdx]?.discounted_cost}`}
                  type="button"
                >
                  {isAddingToCart ? (
                    <>
                      <span className={styles.loadingSpinner} aria-hidden="true"></span>
                      <span className="sr-only">Processing purchase...</span>
                    </>
                  ) : (
                    "BUY NOW"
                  )}
                </button>
              </div>
            )}
            {/* Lazy load heavy components with Suspense and error boundaries */}
            <Suspense fallback={<div className={styles.loadingPlaceholder} role="status" aria-label="Loading benefits...">Loading benefits...</div>}>
              {benefits && (
                <section className={styles.benefitSection} aria-labelledby="benefits-heading">
                  <DigitalBrandBenefit benefits={benefits} />
                </section>
              )}
            </Suspense>
            
            <Suspense fallback={<div className={styles.loadingPlaceholder} role="status" aria-label="Loading FAQs...">Loading FAQs...</div>}>
              {faqList && faqList.length ? (
                <section className={styles.faqsSection} aria-labelledby="faq-heading">
                  <DigitalBrandFaq faqList={faqList} />
                </section>
              ) : null}
            </Suspense>
            {selectedPlanIdx !== -1 && <div className={styles.mobSpacer}></div>}
            <div className={styles.spacer}></div>
          </div>
        )}
        {isD2c && (
          <div className={styles.content}>
            <div className={styles.boxContainer}>
              <p className={styles.d2cTitle}>Select a subscription box</p>
              <div className={styles.boxes}>
                {boxes.map((ele, idx) => (
                  <D2CSubscriptionBoxCard key={idx} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
